import { Updater, RowSelectionState } from '@tanstack/react-table';
import { LocalizedText, lt, showSuccessAlert, useDialogAction, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { PortfolioRateOverview, PortfolioRateType } from '@mews-commander/data-access';
import { DropDownButton, DropdownItem, IconName, ListDivider } from '@optimus-web/core';
import { EditRateContainer } from '../../sections/components/rateForm/EditRateContainer';
import { useDeleteRates, useRateEdit } from '../../../queries';
import { RateFormProvider } from '../../sections/components/rateForm/hooks/RateFormProvider';
import { CreateVoucherContainer } from '../../sections/components/vouchers/CreateVoucherContainer';
import { DELETE_RATE_STARTING_POINT, EDIT_RATE_SINGLE_STARTING_POINT } from '../../../utilities/userFlowTestIds';
import { DeleteRatesDialog } from './DeleteRatesDialog';

interface RowActionsProps {
    rate: PortfolioRateOverview;
    setRowSelection: (updater: Updater<RowSelectionState>) => void;
}

export const RowActions = ({ rate, setRowSelection }: RowActionsProps) => {
    const isVouchersFromRatesEnabled = useFeatureFlag(FeatureFlag.vouchersFromRatesEnabled);

    const editAction = useDialogAction(async () => Promise.resolve());

    const deleteMutation = useDeleteRates();
    const deleteAction = useDialogAction(async () => {
        await deleteMutation.mutateAsync(
            { rateIds: [rate.id] },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('RatesDeletedMessage', {
                            numberOfRates: 1,
                            rateName: rate.name,
                        }),
                    });
                    setRowSelection(({ [rate.id]: removed, ...otherSelection }) => otherSelection);
                },
            }
        );
    });

    const editMutation = useRateEdit();

    const createVoucherAction = useDialogAction(async () => Promise.resolve());

    const showEditRateSuccess = () => {
        showSuccessAlert({
            title: lt('RatesEditedMessage', {
                numberOfRates: 1,
                rateName: rate.name,
            }),
        });
    };

    const toggleStatus = () => {
        editMutation.mutate(
            {
                rateIds: [rate.id],
                isEnabled: {
                    value: !rate.isEnabled,
                },
            },
            {
                onSuccess: showEditRateSuccess,
            }
        );
    };

    const toggleType = () => {
        editMutation.mutate(
            {
                rateIds: [rate.id],
                type: {
                    value:
                        rate.type === PortfolioRateType.Public ? PortfolioRateType.Private : PortfolioRateType.Public,
                },
            },
            {
                onSuccess: showEditRateSuccess,
            }
        );
    };
    return (
        <>
            <DropDownButton icon={IconName.More} variant="ghost" size="small">
                <DropdownItem
                    data-test-id={EDIT_RATE_SINGLE_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.Edit} />}
                    onClick={editAction.handlers.handleOpen}
                />
                <ListDivider />
                {isVouchersFromRatesEnabled && rate.type !== PortfolioRateType.Public && rate.key != null && (
                    <DropdownItem
                        label={<LocalizedText textKey={TextKey.CreateVoucher} />}
                        onClick={createVoucherAction.handlers.handleOpen}
                    />
                )}
                <DropdownItem
                    label={<LocalizedText textKey={rate.isEnabled ? TextKey.Deactivate : TextKey.Activate} />}
                    onClick={toggleStatus}
                />
                <DropdownItem
                    label={
                        <LocalizedText
                            textKey={rate.type === PortfolioRateType.Public ? TextKey.MakePrivate : TextKey.MakePublic}
                        />
                    }
                    onClick={toggleType}
                />
                <ListDivider />
                <DropdownItem
                    data-test-id={DELETE_RATE_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.Delete} />}
                    onClick={deleteAction.handlers.handleOpen}
                    variant="danger"
                />
            </DropDownButton>

            {editAction.states.isDialogOpened && (
                <RateFormProvider enterpriseIds={[rate.propertyId]} selectedRateIds={[rate.id]}>
                    <EditRateContainer
                        areRatesDependent={!rate.isBaseRate}
                        selectedRateIds={[rate.id]}
                        selectedRateOverviews={[rate]}
                        {...editAction}
                    />
                </RateFormProvider>
            )}

            {deleteAction.states.isDialogOpened && <DeleteRatesDialog rates={[rate]} deleteAction={deleteAction} />}

            {isVouchersFromRatesEnabled && createVoucherAction.states.isDialogOpened && (
                <CreateVoucherContainer
                    rateKeys={rate.key ? [rate.key] : undefined}
                    enterpriseIds={[rate.propertyId]}
                    isOpened={createVoucherAction.states.isDialogOpened}
                    onConfirm={createVoucherAction.handlers.handleConfirm}
                    onCancel={createVoucherAction.handlers.handleCancel}
                />
            )}
        </>
    );
};
